import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Parceiro, NovoParceiro, Cupom, NovoCupom } from '@pgcustom/shared-types';

/** Campos aceitos por `PATCH /api/v1/parceiros/:id` (`atualizarParceiroSchema`) — todos opcionais, `null` limpa o campo. */
export interface AtualizarParceiroInput {
  nome?: string;
  ativo?: boolean;
  email?: string | null;
  telefone?: string | null;
  documento?: string | null;
  chavePix?: string | null;
}

/**
 * Adapter do módulo Parceiros no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/parceiros/*`
 * (`apps/api/src/modules/parceiros/parceiros.routes.ts`). Mesmo padrão
 * de `ClientesApi`: `listar`/`listarCupons` devolvem `Resultado<T>`
 * (alimentam estados de UI diretamente); `criar`/`atualizar`/
 * `criarCupom` propagam `ApiError` — quem decide como exibir a falha é
 * a tela que chama.
 */
export class ParceirosApi {
  constructor(private readonly api: ApiClient) {}

  async listar(): Promise<Resultado<Parceiro[]>> {
    try {
      const resposta = await this.api.get<{ parceiros: Parceiro[] }>('/parceiros');
      if (resposta.parceiros.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.parceiros };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar parceiros' };
    }
  }

  async criar(dado: NovoParceiro): Promise<Parceiro> {
    const resposta = await this.api.post<{ parceiro: Parceiro }>('/parceiros', dado);
    return resposta.parceiro;
  }

  async atualizar(id: string, dado: AtualizarParceiroInput): Promise<Parceiro> {
    const resposta = await this.api.patch<{ parceiro: Parceiro }>(`/parceiros/${id}`, dado);
    return resposta.parceiro;
  }

  async listarCupons(parceiroId: string): Promise<Resultado<Cupom[]>> {
    try {
      const resposta = await this.api.get<{ cupons: Cupom[] }>(`/parceiros/${parceiroId}/cupons`);
      if (resposta.cupons.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.cupons };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar cupons' };
    }
  }

  async criarCupom(parceiroId: string, dado: NovoCupom): Promise<Cupom> {
    const resposta = await this.api.post<{ cupom: Cupom }>(`/parceiros/${parceiroId}/cupons`, dado);
    return resposta.cupom;
  }
}
